import { Row, Col, Panel } from 'react-bootstrap';
import { connect } from 'react-redux';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { browserHistory } from 'react-router';
import StripeCheckout from 'react-stripe-checkout';

import Button from './common/Button';
import TextInput from './common/TextInput';
import ConfirmDialog from './common/ConfirmDialog';
import ConditionsAndPolicyLinks from './common/ConditionsAndPolicyLinks';
import { logInUserFormAction,
         userSubscriptionValidateCoupon,
         forgotPasswordUserFormAction } from '../actions/userActions';

import { isEmptyObject } from '../validators/formValidators';

import { validateLogInForm, validateEmailForm } from '../validators/logInFormValidator';
import { caseSetServerError } from '../actions/caseActions';
import { retrieveNotifications,
         getCouponInfo,
         isValidSubscriptionStatus,
         isAdmin,
         findBootstrapEnvironment } from '../helpers/utils';

export const fields = ['Email', 'Password', 'Coupon', 'TwoFactorToken'];

class LogInForm extends PureComponent {
    constructor(props) {
        super(props);
        this.logIn = this.logIn.bind(this);
        this.logInWithNewCard = this.logInWithNewCard.bind(this);
        this.signUp = this.signUp.bind(this);
        this.paymentDialogConfirm = this.paymentDialogConfirm.bind(this);
        this.resetPassword = this.resetPassword.bind(this);
        this.state = {
            submitting: false,
            paymentDialogOpen: false,
            twoFactorAuthDialogOpen: false,
            resetPasswordDialogOpen: false,
            loadingResetPassword: false,
            resetPasswordDialogSuccess: false,
            paymentDialogText: undefined,
            couponDescription: undefined,
            selectedPrice: process.env.SUBSCRIPTION_PRIVATE_BETA_COST,
            finalPrice: process.env.SUBSCRIPTION_PRIVATE_BETA_COST,
        };
    }

    paymentDialogConfirm() {
        const coupon = this.props.values.Coupon;
        this.props.dispatch(userSubscriptionValidateCoupon(coupon))
            .then((result) => {
                if (!isEmptyObject(result.data)) {
                    const couponInfo = getCouponInfo(result.data, this.state.selectedPrice);
                    if (!couponInfo) {
                        this.props.dispatch(caseSetServerError('Error processing coupon'));
                        return;
                    }

                    this.setState({
                        finalPrice: couponInfo.finalPrice,
                        couponDescription: couponInfo.couponDescription,
                    });
                } else {
                    this.setState({
                        finalPrice: this.state.selectedPrice,
                        couponDescription: undefined,
                    });
                }

                this.setState({ paymentDialogOpen: false });
                this.refs.StripeCheckout.onClick();
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            });
    }

    signUp(evt) {
        evt.preventDefault();
        if (this.props.registrationInPopup) {
            window.open('/user/signup', 'popup');
            return;
        }

        browserHistory.push('/user/signup');
    }

    logInWithNewCard(token) {
        const data = this.props.values;
        data['$StripeCardToken$'] = token;
        this.logIn(data);
    }

    logIn(data) {
        if (!data) {
            data = this.props.values;
        }

        const errors = validateLogInForm(data);
        const allowRegistration = this.props.allowRegistration === undefined ? true : this.props.allowRegistration;
        if (!isEmptyObject(errors)) {
            Object.keys(errors).map((key) => { this.props.setFieldTouched(key, true, false); });
            this.props.setErrors(errors);
            return undefined;
        }

        this.setState({ submitting: true });
        return new Promise((resolve, reject) => {
            this.props.dispatch(logInUserFormAction(data))
                .then((result) => {
                    if (result) {
                        const user = result.data.user;
                        if (isValidSubscriptionStatus(user)) {
                            this.props.dispatch(caseSetServerError(undefined));
                            if (this.props.onSuccess) {
                                this.props.onSuccess(result);
                                return;
                            }

                            retrieveNotifications(this.props.dispatch);
                            browserHistory.push(isAdmin(user) ? '/admin' : '/case/all');
                            resolve(result);
                            return;
                        }

                        const twoFactorAuth = result.data.twoFactorAuth;
                        if (twoFactorAuth === 'request_token') {
                            this.setState({ twoFactorAuthDialogOpen: true });
                            resolve('Validate token...');
                        } else if (twoFactorAuth === 'failure') {
                            this.setState({ twoFactorAuthDialogOpen: false });
                            this.props.resetForm();
                            this.props.dispatch(caseSetServerError('Token validation failed. Try again.'));
                            resolve('Token validation failure.');
                        } else if (allowRegistration) {
                            this.setState({
                                paymentDialogText: 'The subscription is inactive. A valid credit/debit card needs to be provided.\nOptionally provide a coupon if you have any to the field below.',
                                paymentDialogOpen: true,
                            });

                            resolve('Subscription not active.');
                        } else {
                            this.props.dispatch(caseSetServerError('Invalid username/password or subscription inactive.'));
                            resolve('Token validation failure.');
                        }
                    }

                    this.setState({ submitting: false });
                })
                .catch((error) => {
                    console.error(new Error().stack, error);
                    this.setState({ submitting: false, twoFactorAuthDialogOpen: false });
                    this.props.dispatch(caseSetServerError(error));
                    if (this.props.onError) {
                        this.props.onError(error);
                    }

                    reject(error);
                });
        });
    }

    resetPassword() {
        const data = this.props.values;
        if (!isEmptyObject(validateEmailForm(data))) {
            return undefined;
        }

        this.setState({ loadingResetPassword: true });
        return new Promise((resolve, reject) => {
            this.props.dispatch(forgotPasswordUserFormAction(data))
                .then((result) => {
                    if (result) {
                        resolve(result);
                    }

                    this.setState({ loadingResetPassword: false, resetPasswordDialogSuccess: true });
                })
                .catch((error) => {
                    console.error(new Error().stack, error);
                    this.setState({ loadingResetPassword: false });
                    this.props.dispatch(caseSetServerError(error));
                    reject(error);
                });
        });
    }

    render() {
        const {
            handleSubmit,
            handleChange,
            handleBlur,
            values = {},
            errors,
            touched,
            allowRegistration = true,
            hidePolicy = false,
            hideForgotPassword = false,
            registrationInPopup = false,
            showPanel = true,
        } = this.props;

        let fullWidth = this.props.fullWidth || false;
        const size = findBootstrapEnvironment();
        if (size === 'xs' || size === 'sm') {
            fullWidth = true;
        }

        const submitting = this.state.submitting;
        let panel = (
            <div>
                <Row>
                    <Col xs={12}>
                        <TextInput field={{
                                       name: 'Email',
                                       onChange: handleChange,
                                       onBlur: handleChange,
                                       error: errors['Email'],
                                       touched: touched['Email'],
                                       value: values['Email'],
                                   }}
                                   placeholder="Enter your email"
                                   label="Email"
                                   disabled={submitting}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <TextInput field={{
                                       name: 'Password',
                                       onChange: handleChange,
                                       onBlur: handleChange,
                                       error: errors['Password'],
                                       touched: touched['Password'],
                                       value: values['Password'],
                                   }}
                                   placeholder="Secret between the two of us"
                                   label="Password"
                                   disabled={submitting}
                                   type="password"
                        />
                    </Col>
                </Row>
                <br />
                <Row>
                    <Col xs={12}>
                    <div style={{float: 'left', marginLeft: 15, width: '150px'}}>
                        <Button block
                                type="submit"
                                bsStyle="primary"
                                label="Log In"
                                loading={submitting}
                                disabled={submitting}
                                loadingLabel="Logging in..."
                        />
                    </div>
                    {allowRegistration && (
                    <div style={{float: 'right'}}>
                        <Button onClick={this.signUp}
                                bsStyle="link"
                                disabled={submitting}
                                label="Sign Up"
                        />
                    </div>)}
                    </Col>
                </Row>
                {!hideForgotPassword && (
                <Row>
                    <Col xs={fullWidth ? 12 : 6} lg={fullWidth ? 12 : 3} md={fullWidth ? 12 : 4}>
                    <Button onClick={() => this.setState({ resetPasswordDialogOpen: true, resetPasswordDialogSuccess: false })}
                            bsStyle="link"
                            disabled={submitting}
                            label={(<h6 style={{ paddingTop: 10 }}>Forgot password</h6>)}
                    />
                    </Col>
                </Row>)}
            </div>);

        if (showPanel) {
            panel = (
            <Panel data-hj-whitelist bsStyle="primary" header="Log In">
                {panel}            
            </Panel>);
        }
        
        return (
            <form ref="logInForm" onSubmit={(e) => { e.preventDefault(); this.logIn(values); }}>
                <Row>
                    <Col md={fullWidth ? 0 : 3} xs={fullWidth ? 0 : 2} />
                    <Col md={fullWidth ? 12 : 6} xs={fullWidth ? 12 : 8}>
                    {panel}
                    {!hidePolicy && (
                    <Row>
                        <Col xs={fullWidth ? 12 : 6} md={fullWidth ? 12 : 6}>
                        <ConditionsAndPolicyLinks disabled={false} />
                        </Col>
                    </Row>)}
                    </Col>
                    {allowRegistration && !registrationInPopup && (
                    <div>
                    <StripeCheckout ref="StripeCheckout"
                                    token={this.logInWithNewCard}
                                    stripeKey={process.env.STRIPE_PUBLIC_API_KEY}
                                    name="Case Tempo"
                                    panelLabel="Subscribe"
                                    amount={parseInt(this.state.finalPrice, 10)}
                                    description={this.state.couponDescription}
                                    billingAddress
                                    zipCode
                                    currency="USD"
                                    ComponentClass="div"
                                    allowRememberMe={false}
                                    email={values['Email']}
                                    closed={() => this.setState({ submitting: this.state.submitting })}
                    ><div />
                    </StripeCheckout>
                    <ConfirmDialog title="Subscription inactive"
                                   text={this.state.paymentDialogText}
                                   cancel={() => { this.setState({ paymentDialogOpen: false }); }}
                                   confirm={this.paymentDialogConfirm}
                                   open={this.state.paymentDialogOpen}
                    >
                        <Row>
                        <Col xs={12}>
                        <TextInput field={{
                                       name: 'Coupon',
                                       onChange: handleChange,
                                       onBlur: handleBlur,
                                       error: errors['Coupon'],
                                       touched: touched['Coupon'],
                                       value: values['Coupon'],
                                   }}
                                   label="Discount Code"
                        />
                        </Col>
                        </Row>
                    </ConfirmDialog>
                    </div>)}
                    <ConfirmDialog title="Two Factor Authentication"
                                   cancel={() => { this.setState({ twoFactorAuthDialogOpen: false }); }}
                                   confirm={() => {
                                       this.logIn(values);
                                   }}
                                   text="Enter the token from the SMS or the authenticator app."
                                   open={this.state.twoFactorAuthDialogOpen}
                    >
                        <Row>
                        <Col xs={12} style={{ marginTop: 15 }}>
                        <TextInput field={{
                                       name: 'TwoFactorToken',
                                       onChange: handleChange,
                                       onBlur: handleBlur,
                                       error: errors['TwoFactorToken'],
                                       touched: touched['TwoFactorToken'],
                                       value: values['TwoFactorToken'],
                                   }}
                                   label="Token"
                        />
                        </Col>
                        </Row>
                    </ConfirmDialog>
                    <ConfirmDialog title="Reset Password"
                                   cancel={() => { this.setState({ resetPasswordDialogOpen: false }); }}
                                   confirmLabel={this.state.resetPasswordDialogSuccess ? undefined : 'Reset Password'}
                                   confirm={() => {
                                       if (this.state.resetPasswordDialogSuccess ) {
                                           this.setState({resetPasswordDialogOpen: false});
                                           return;
                                       }

                                       this.resetPassword();
                                   }}
                                   disabled={this.state.loadingResetPassword}
                                   open={this.state.resetPasswordDialogOpen}
                    >
                        <Row>
                        <Col xs={12} style={{ marginTop: 15 }}>
                        {this.state.resetPasswordDialogSuccess && (
                            <h6>Password has been reset. Please check your email for new password.</h6>
                        )}
                        {!this.state.resetPasswordDialogSuccess && (
                        <TextInput field={{
                                       name: 'Email',
                                       onChange: handleChange,
                                       onBlur: handleBlur,
                                       error: errors['Email'],
                                       touched: touched['Email'],
                                       value: values['Email'],
                                   }}
                                   label="Email"
                                   disabled={this.state.loadingResetPassword}
                        />)}
                        </Col>
                        </Row>
                    </ConfirmDialog>
                </Row>
            </form>
        );
    }
}

LogInForm.propTypes = {
    Email: PropTypes.object,
    Password: PropTypes.object,
    serverError: PropTypes.string,
    submitting: PropTypes.bool.isRequired,
    user: PropTypes.object,
    token: PropTypes.string,
    allowRegistration: PropTypes.bool,
    showPanel: PropTypes.string,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
    fullWidth: PropTypes.bool,
    registrationInPopup: PropTypes.bool,
    hidePolicy: PropTypes.bool,
    hideForgotPassword: PropTypes.bool,
};


const formikWrapper = {
    mapPropsToValues: () => { return { 'Email': '', 'Password': '', 'Coupon': '', 'TwoFactorToken': '' }; },
    validate: validateLogInForm,
};

export default connect()(withFormik(formikWrapper)(LogInForm));

